/* eslint-disable import/no-unresolved */
import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router-dom";
import { push } from "react-router-redux";
import parse from "html-react-parser";
import { bool, func, shape, string } from "prop-types";

import { LoginAcceptToc } from "@ui-kit/components/index.es";
import { actions as layoutActions } from "reducers/layout";
import { actions as loginActions, selectors as loginSelectors } from "reducers/login";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as configSelectors } from "reducers/config";
import * as i18n from "util/i18n";
import { sanitizeHtml } from "util/xss";

import withExchangeToken from "pages/_components/withExchangeToken";
import { resizableRoute } from "pages/_components/Resizable";
import { getPostLoginHomePath, getUnauthenticatedLoginPath } from "util/v3UxConfig";
import { isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";

import { LOGIN_ACCEPT_TOC_LABELS_KEYS } from "./loginI18nKeys";

function resolveTermsContent(loginState) {
    if (!loginState) {
        return null;
    }

    return (
        loginState.termsAndConditions?.body ||
        loginState.generalConditions?.text ||
        loginState.generalConditions?.body ||
        null
    );
}

function hasPendingTerms(loginState) {
    return Boolean(resolveTermsContent(loginState) || loginState?.generalConditions || loginState?.termsAndConditions);
}

function LoginStep4V3({
    dispatch,
    termsContentHtml,
    hasTerms,
    exchangeToken,
    activeEnvironment,
    isSubmitting,
    fetchingDownload,
    uxLoginImprovements,
    uxDashboardImprovements,
    location,
}) {
    useEffect(() => {
        dispatch(layoutActions.setScopes(["loginStep4"]));
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(loginActions.resetLoading());
        };
    }, [dispatch]);

    useEffect(() => {
        const pathname = location?.pathname || "";
        const onStep4Path =
            pathname === "/v3/login/step4" ||
            pathname === "/v3/login/step4/" ||
            pathname === "/loginStep4" ||
            pathname === "/loginStep4/";

        if (!onStep4Path) {
            return;
        }

        if (activeEnvironment) {
            dispatch(push(getPostLoginHomePath(uxDashboardImprovements)));
            return;
        }

        if (!hasTerms || !exchangeToken) {
            dispatch(push(getUnauthenticatedLoginPath(uxLoginImprovements)));
        }
    }, [
        dispatch,
        hasTerms,
        exchangeToken,
        activeEnvironment,
        uxLoginImprovements,
        uxDashboardImprovements,
        location?.pathname,
    ]);

    const getLabels = useCallback((key) => i18n.get(LOGIN_ACCEPT_TOC_LABELS_KEYS[key] ?? key), []);

    const handleAccept = useCallback(() => {
        dispatch(loginActions.loginStep4(true, null));
    }, [dispatch]);

    const handleDecline = useCallback(() => {
        dispatch(loginActions.reset());
    }, [dispatch]);

    const handleDownload = useCallback(() => {
        dispatch(loginActions.downloadDocumentRequest({ format: "pdf" }));
    }, [dispatch]);

    const emptyContentLabel = useMemo(() => i18n.get("global.termsAndConditions.content"), []);

    const termsContentNode = useMemo(() => {
        if (!termsContentHtml) {
            return null;
        }
        return parse(sanitizeHtml(termsContentHtml));
    }, [termsContentHtml]);

    if (!hasTerms) {
        return null;
    }

    return (
        <LoginAcceptToc
            termsContent={termsContentNode}
            getLabels={getLabels}
            onAccept={handleAccept}
            onDecline={handleDecline}
            onDownload={handleDownload}
            submitLoading={isSubmitting}
            fetchingDownload={fetchingDownload}
            emptyContentLabel={emptyContentLabel}
        />
    );
}

LoginStep4V3.propTypes = {
    dispatch: func.isRequired,
    termsContentHtml: string,
    hasTerms: bool.isRequired,
    exchangeToken: string,
    activeEnvironment: shape({}),
    isSubmitting: bool.isRequired,
    fetchingDownload: bool.isRequired,
    uxLoginImprovements: bool,
    uxDashboardImprovements: bool,
    location: shape({ pathname: string }).isRequired,
};

LoginStep4V3.defaultProps = {
    termsContentHtml: null,
    exchangeToken: null,
    activeEnvironment: null,
    uxLoginImprovements: false,
    uxDashboardImprovements: false,
};

const mapStateToProps = (state) => ({
    termsContentHtml: resolveTermsContent(state.login),
    hasTerms: hasPendingTerms(state.login),
    exchangeToken: loginSelectors.getExchangeToken(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    isSubmitting: loginSelectors.getFetching(state),
    fetchingDownload: loginSelectors.getFetchingDownload(state),
    uxLoginImprovements: !!configSelectors.getConfigKey(state, "frontend.UXImprovements.Login.functionalities"),
    uxDashboardImprovements: isUxDashboardImprovementsEnabled(),
});

export default compose(withRouter, connect(mapStateToProps), resizableRoute, withExchangeToken)(LoginStep4V3);
